import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, Envelope, Eye } from "@phosphor-icons/react";
import { CoverageCard, MiniStat } from "./panels-shared";

export default function OutreachPanel({ advancedMode }) {
  const [outreach, setOutreach] = useState({ runs: [], logs: [], running: false });
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSlug, setPreviewSlug] = useState("");

  const load = useCallback(async () => {
    try {
      const [runs, logs] = await Promise.all([
        api.get("/admin/outreach/runs"),
        api.get("/admin/outreach/logs", { params: { limit: 50 } }),
      ]);
      setOutreach((o) => ({ ...o, runs: runs.data, logs: logs.data }));
    } catch (e) {
      console.error("admin outreach load failed:", e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const triggerOutreach = async () => {
    setOutreach((o) => ({ ...o, running: true }));
    try {
      await api.post("/admin/outreach/trigger");
      toast.success("Pipeline outreach IA déclenché — rafraîchissez dans quelques instants");
      setTimeout(load, 3000);
    } catch {
      toast.error("Erreur déclenchement outreach");
    }
    setOutreach((o) => ({ ...o, running: false }));
  };

  const previewEmail = async (slug) => {
    setPreviewHtml("loading");
    try {
      let targetSlug = slug;
      if (!targetSlug) {
        const r0 = await api.get("/admin/outreach/prospects", { params: { limit: 1 } });
        targetSlug = r0.data?.items?.[0]?.slug;
        if (targetSlug) setPreviewSlug(targetSlug);
      }
      if (!targetSlug) {
        toast.error("Entrez un slug d'entreprise (ex: boulangerie-colmar-012345).");
        setPreviewHtml(null);
        return;
      }
      const r = await api.get(`/admin/outreach/preview/${targetSlug}`);
      setPreviewHtml(r.data.html);
      toast.success(`Aperçu généré pour ${targetSlug}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur preview IA");
      setPreviewHtml(null);
    }
  };

  return (
    <div data-testid="admin-outreach" className="space-y-6">
      <EmailScraperPanel advancedMode={advancedMode} />

      {advancedMode && <ScrapePanel />}

      <div className="bg-card border border-border rounded-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="label-uppercase mb-1 flex items-center gap-2"><PaperPlaneTilt size={14}/> Pipeline outreach IA</div>
            <div className="text-sm text-muted-foreground">Lance automatique tous les matins 09:00 UTC — emails personnalisés FR/EN/DE générés par IA. Cap quotidien : <span className="font-mono text-foreground font-semibold">1000 emails/jour</span> (Resend Pro actif).</div>
          </div>
          {advancedMode ? (
            <button onClick={triggerOutreach} disabled={outreach.running} className="bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-2.5 rounded-sm text-sm font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="trigger-outreach">
              <PaperPlaneTilt size={14}/> {outreach.running ? "Lancement…" : "Lancer maintenant"}
            </button>
          ) : (
            <span className="text-xs text-muted-foreground italic whitespace-nowrap">✅ Envoi automatique quotidien actif</span>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-border font-display font-medium">Derniers passages</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[480px]">
              <thead className="bg-accent/30 text-xs uppercase tracking-wider text-muted-foreground">
                <tr><th className="text-left px-4 py-2">Démarré</th><th className="text-right px-4 py-2">Envoyés</th><th className="text-right px-4 py-2">Appels</th><th className="text-right px-4 py-2">Err.</th></tr>
              </thead>
              <tbody>
                {outreach.runs.map((r, i) => (
                  <tr key={r.started_at || i} className="border-t border-border">
                    <td className="px-4 py-2 text-xs">{r.started_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="text-right px-4 py-2 tabular-nums font-semibold">{r.sent}</td>
                    <td className="text-right px-4 py-2 tabular-nums">{r.call_list}</td>
                    <td className="text-right px-4 py-2 tabular-nums text-destructive">{r.errors}</td>
                  </tr>
                ))}
                {outreach.runs.length === 0 && <tr><td colSpan="4" className="text-center text-muted-foreground py-6">Pas encore de passage. Cliquez « Lancer maintenant ».</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-border font-display font-medium flex items-center gap-2"><Envelope size={16}/> Derniers emails envoyés</div>
          <div className="max-h-[400px] overflow-y-auto">
            {outreach.logs.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground text-sm">Aucun email envoyé pour le moment.</div>
            ) : outreach.logs.map((l) => (
              <div key={l.business_id + l.email_sent_at} className="border-t border-border p-4 first:border-t-0" data-testid={`outreach-log-${l.business_slug}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{l.subject}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      → {l.email} · <a href={`/entreprise/${l.business_slug}`} target="_blank" rel="noreferrer" className="text-primary underline">{l.business_slug}</a>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{l.email_sent_at?.slice(0, 16).replace("T", " ")} · {l.language?.toUpperCase()} · <span className={l.status === "sent" ? "text-primary" : "text-destructive"}>{l.status}</span></div>
                  </div>
                  <button onClick={() => { setPreviewSlug(l.business_slug); previewEmail(l.business_slug); }} className="p-1.5 border border-border hover:border-foreground rounded-sm shrink-0" title="Aperçu" data-testid="preview-email-btn">
                    <Eye size={14}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <input value={previewSlug} onChange={(e) => setPreviewSlug(e.target.value)} placeholder="Slug d'une entreprise (ex: nom-ville-123456)" className="flex-1 border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary" data-testid="preview-slug-input"/>
          <button onClick={() => previewEmail(previewSlug)} className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-4 py-2 rounded-sm text-sm font-semibold" data-testid="preview-generate">Générer aperçu IA</button>
        </div>
        {previewHtml === "loading" && <div className="text-muted-foreground text-sm">Génération par l&apos;IA… (~5s)</div>}
        {previewHtml && previewHtml !== "loading" && (
          <iframe title="Email preview" srcDoc={previewHtml} className="w-full h-[680px] border border-border rounded-sm bg-white" data-testid="email-preview-iframe"/>
        )}
      </div>
    </div>
  );
}

function ScrapePanel() {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/scrape/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const itv = setInterval(load, 5000);
    return () => clearInterval(itv);
  }, [load]);

  const run = async () => {
    setBusy(true);
    try {
      await api.post("/admin/scrape/run", null, { params: { limit: 500 } });
      toast.success("Enrichissement web lancé — 500 fiches ciblées.");
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setBusy(false);
  };

  if (!status) return null;
  const running = status.state?.running;
  const c = status.coverage || {};
  const pctEmail = c.total ? Math.round((c.with_email / c.total) * 100) : 0;
  const pctPhone = c.total ? Math.round((c.with_phone / c.total) * 100) : 0;
  const pctSite = c.total ? Math.round((c.with_website / c.total) * 100) : 0;

  return (
    <div className="bg-card border-2 border-secondary rounded-sm p-6" data-testid="scrape-panel">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5">
        <div>
          <div className="label-uppercase mb-1 text-secondary">Enrichissement contacts (scraper web)</div>
          <div className="font-display text-lg font-medium mb-1">Récupérer emails + téléphones</div>
          <div className="text-xs text-muted-foreground">
            L&apos;API INSEE ne fournit pas d&apos;emails. Ce scraper visite les sites web des entreprises pour extraire leurs coordonnées (lot de 500 fiches par clic, ~5 min).
          </div>
        </div>
        <button onClick={run} disabled={busy || running} className="bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-2.5 rounded-sm text-sm font-semibold disabled:opacity-60 whitespace-nowrap" data-testid="scrape-run-btn">
          {running ? "En cours…" : busy ? "Démarrage…" : "Scraper 500 fiches"}
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <CoverageCard label="Avec email" value={c.with_email || 0} total={c.total || 0} pct={pctEmail} color="primary"/>
        <CoverageCard label="Avec téléphone" value={c.with_phone || 0} total={c.total || 0} pct={pctPhone} color="secondary"/>
        <CoverageCard label="Avec site web" value={c.with_website || 0} total={c.total || 0} pct={pctSite} color="foreground"/>
      </div>
      {running && status.state?.progress && (
        <div className="mt-4 text-xs text-muted-foreground flex items-center gap-2" data-testid="scrape-progress">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-secondary" />
          </span>
          Progression : {status.state.progress.done}/{status.state.progress.target} · emails trouvés : {status.state.progress.found_email} · tél : {status.state.progress.found_phone}
        </div>
      )}
    </div>
  );
}

function EmailScraperPanel({ advancedMode }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/enrichment/email-scrape/status");
      setData(r.data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    load();
    const itv = setInterval(load, 30000);
    return () => clearInterval(itv);
  }, [load]);

  const trigger = async () => {
    setBusy(true);
    try {
      await api.post("/admin/enrichment/email-scrape/trigger");
      toast.success("Scraper email lancé en arrière-plan (~10-15 min).");
      setTimeout(load, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const pctSites = data.with_email && data.with_website ? Math.round((data.with_email / data.with_website) * 100) : 0;
  const last = data.last_run;

  return (
    <div className="bg-card border-2 border-primary/40 rounded-sm p-6" data-testid="email-scraper-panel">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
        <div className="flex-1">
          <div className="label-uppercase mb-1 text-primary flex items-center gap-2"><Envelope size={14}/> Récolte d&apos;emails (sites web)</div>
          <div className="font-display text-lg font-medium mb-1">Carburant du pipeline outreach</div>
          <div className="text-xs text-muted-foreground">
            Visite les sites web des entreprises pour extraire emails publics. <strong>Sans emails, l&apos;outreach IA n&apos;a rien à envoyer.</strong> Cron auto à 04:00 UTC ({data.daily_cap} sites/jour).
          </div>
        </div>
        {advancedMode && (
          <button onClick={trigger} disabled={busy} className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-4 py-2.5 rounded-sm text-sm font-semibold disabled:opacity-60 whitespace-nowrap" data-testid="email-scrape-run-btn">
            {busy ? "Lancement…" : "Scraper maintenant"}
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <CoverageCard label="Avec email" value={data.with_email} total={data.with_website} pct={pctSites} color="primary"/>
        <MiniStat label="Sites web" value={data.with_website} hint={`/ ${data.total.toLocaleString("fr-FR")}`}/>
        <MiniStat label="Tentatives" value={data.scraped_attempts}/>
        <MiniStat label="Restant à scraper" value={data.pending_to_scrape} hint={data.pending_to_scrape > 0 ? `~${Math.ceil(data.pending_to_scrape / Math.max(1, data.daily_cap))} jours` : "Terminé"}/>
      </div>

      {last && (
        <div className="text-xs text-muted-foreground border-t border-border pt-3 mt-2">
          Dernier passage : {last.started_at?.slice(0, 16).replace("T", " ")} ·{" "}
          <span className="font-semibold text-foreground tabular-nums">{last.found_email || 0}</span> emails trouvés sur {last.candidates || last.cap} sites
          {last.cap > 0 && last.candidates > 0 && (
            <span className="text-muted-foreground"> ({Math.round(((last.found_email || 0) / last.candidates) * 100)}% hit rate)</span>
          )}
        </div>
      )}
    </div>
  );
}
